import { useEffect, useState } from 'react';
import { cloudApi } from '../services/api';
import {
  buildCloudStorageMeta,
  isLiveCloudProvider,
  type CloudStorageMeta,
  type Tier2SelectedFile,
} from '../lib/cloudStorage';

interface Tier2DocumentPickerProps {
  scope?: 'user' | 'org';
  cloud: CloudStorageMeta;
  category?: string;
  buttonLabel?: string;
  onFilesChange?: (files: Tier2SelectedFile[]) => void;
}

export default function Tier2DocumentPicker({
  scope = 'user',
  cloud,
  category = 'General',
  buttonLabel = 'Select General Documents',
  onFilesChange,
}: Tier2DocumentPickerProps) {
  const [selected, setSelected] = useState<Tier2SelectedFile[]>([]);
  const [driveFiles, setDriveFiles] = useState<Array<{ id: string; name: string; mimeType?: string }>>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const liveGoogle = isLiveCloudProvider(cloud.provider) && cloud.connected && Boolean(cloud.folder_id);

  useEffect(() => {
    cloudApi.getSelectedFiles(scope)
      .then((data) => {
        const files = (data.files || []) as Tier2SelectedFile[];
        const next = category === 'Emergency Protocol'
          ? files.filter((file) => file.category === 'Emergency Protocol')
          : files.filter((file) => file.category !== 'Emergency Protocol');
        setSelected(next);
        onFilesChange?.(files);
      })
      .catch(() => {});
  }, [scope, category]);

  const persist = async (files: Tier2SelectedFile[]) => {
    const existing = await cloudApi.getSelectedFiles(scope).catch(() => ({ files: [] }));
    const prior = (existing.files || []) as Tier2SelectedFile[];
    const kept = prior.filter((file) => {
      if (category === 'Emergency Protocol') return file.category !== 'Emergency Protocol';
      return file.category === 'Emergency Protocol';
    });
    const merged = [...kept, ...files];
    await cloudApi.saveSelectedFiles({ scope, files: merged });
    setSelected(files);
    onFilesChange?.(merged);
  };

  const openLocalStubPicker = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = category !== 'Emergency Protocol';
    input.onchange = () => {
      const list = input.files ? Array.from(input.files) : [];
      const uploadedAt = new Date().toISOString();
      const mapped: Tier2SelectedFile[] = list.map((file) => ({
        drive_file_id: `local-intent-${file.name}-${Date.now()}`,
        filename: file.name,
        category,
        selected_at: uploadedAt,
      }));
      void persist(mapped).then(() => {
        setStatus(
          cloud.provider === 'pending'
            ? 'Filename metadata saved. Connect Google Drive to browse the cloud folder.'
            : 'Provider is stubbed — filename metadata only until that OAuth app is registered.',
        );
      });
    };
    input.click();
  };

  const openDrivePicker = async () => {
    setLoading(true);
    setStatus('');
    try {
      const data = await cloudApi.listFiles(scope);
      setDriveFiles(data.files || []);
      setPickerOpen(true);
      if (!(data.files || []).length) {
        setStatus('No files found in the connected Google Drive folder.');
      }
    } catch (error: unknown) {
      const err = error as { error?: string; connected?: boolean };
      setStatus(err.error || 'Unable to browse Google Drive folder.');
      if (err.connected === false) {
        setPickerOpen(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleDriveFile = (file: { id: string; name: string; mimeType?: string }) => {
    setSelected((current) => {
      const exists = current.some((item) => item.drive_file_id === file.id);
      if (category === 'Emergency Protocol') {
        return exists
          ? []
          : [{
            drive_file_id: file.id,
            filename: file.name,
            category,
            mime_type: file.mimeType || null,
            selected_at: new Date().toISOString(),
          }];
      }
      if (exists) return current.filter((item) => item.drive_file_id !== file.id);
      return [
        ...current,
        {
          drive_file_id: file.id,
          filename: file.name,
          category,
          mime_type: file.mimeType || null,
          selected_at: new Date().toISOString(),
        },
      ];
    });
  };

  const saveDriveSelection = async () => {
    setLoading(true);
    try {
      await persist(selected);
      setPickerOpen(false);
      setStatus('Selected Drive file metadata saved. Files remain in your Google Drive.');
    } catch (error: unknown) {
      const err = error as { error?: string };
      setStatus(err.error || 'Unable to save selected files.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="tier2-doc-picker">
      <button
        type="button"
        className="btn-primary pro-setup-upload-btn"
        onClick={() => {
          if (liveGoogle) void openDrivePicker();
          else openLocalStubPicker();
        }}
        disabled={loading}
      >
        {loading ? 'Loading...' : buttonLabel}
      </button>
      <p className="pro-setup-helper-line">
        {liveGoogle
          ? 'Browsing your connected Google Drive folder. Mentology stores filename + Drive file ID only.'
          : 'OAuth upload is stubbed for this provider (or Connect later). Local filename metadata only.'}
      </p>
      {selected.length > 0 && (
        <ul className="pro-setup-upload-list">
          {selected.map((doc) => (
            <li key={doc.drive_file_id}>
              <span>{doc.filename}</span>
              <span>{doc.category || category}</span>
            </li>
          ))}
        </ul>
      )}
      {pickerOpen && (
        <div className="tier2-drive-picker">
          <p className="pro-setup-section-subheading">Connected folder files</p>
          <ul className="pro-setup-upload-list">
            {driveFiles.map((file) => {
              const checked = selected.some((item) => item.drive_file_id === file.id);
              return (
                <li key={file.id}>
                  <label>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleDriveFile(file)}
                    />
                    {' '}
                    {file.name}
                  </label>
                </li>
              );
            })}
          </ul>
          <button
            type="button"
            className="btn-primary pro-setup-upload-btn"
            onClick={() => void saveDriveSelection()}
            disabled={loading}
          >
            Save selected Drive files
          </button>
        </div>
      )}
      {status && <p className="pro-setup-helper-line">{status}</p>}
    </div>
  );
}

export function cloudMetaFromForm(form: {
  cloud_provider: string;
  cloud_folder_label: string;
  cloud_folder_id?: string | null;
  cloud_connected?: boolean;
}): CloudStorageMeta {
  return buildCloudStorageMeta({
    provider: form.cloud_provider,
    folder_label: form.cloud_folder_label,
    folder_id: form.cloud_folder_id,
    connected: form.cloud_connected,
  });
}
